/* tslint:disable */
/* eslint-disable */
/**
 * Selling Partner API for Orders
 * Use the Orders Selling Partner API to programmatically retrieve order information. With this API, you can develop fast, flexible, and custom applications to manage order synchronization, perform order research, and create demand-based decision support tools.   _Note:_ For the JP, AU, and SG marketplaces, the Orders API supports orders from 2016 onward. For all other marketplaces, the Orders API supports orders for the last two years (orders older than this don\'t show up in the response).
 *
 * The version of the OpenAPI document: v0
 * 
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */


// May contain unused imports in some cases
// @ts-ignore
import type { AmazonPrograms } from './amazon-programs.js';
// May contain unused imports in some cases
// @ts-ignore
import type { AssociatedItem } from './associated-item.js';
// May contain unused imports in some cases
// @ts-ignore
import type { BuyerRequestedCancel } from './buyer-requested-cancel.js';
// May contain unused imports in some cases
// @ts-ignore
import type { ItemBuyerInfo } from './item-buyer-info.js';
// May contain unused imports in some cases
// @ts-ignore
import type { Measurement } from './measurement.js';
// May contain unused imports in some cases
// @ts-ignore
import type { Money } from './money.js';
// May contain unused imports in some cases
// @ts-ignore
import type { PointsGrantedDetail } from './points-granted-detail.js';
// May contain unused imports in some cases
// @ts-ignore
import type { ProductInfoDetail } from './product-info-detail.js';
// May contain unused imports in some cases
// @ts-ignore
import type { ShippingConstraints } from './shipping-constraints.js';
// May contain unused imports in some cases
// @ts-ignore
import type { SubstitutionPreferences } from './substitution-preferences.js';
// May contain unused imports in some cases
// @ts-ignore
import type { TaxCollection } from './tax-collection.js';

/**
 * A single order item.
 */
export interface OrderItem {
    /**
     * The item\'s Amazon Standard Identification Number (ASIN).
     */
    'ASIN': string;
    /**
     * The item\'s seller stock keeping unit (SKU).
     */
    'SellerSKU'?: string;
    /**
     * An Amazon-defined order item identifier.
     */
    'OrderItemId': string;
    /**
     * A list of associated items that a customer has purchased with a product. For example, a tire installation service purchased with tires.
     */
    'AssociatedItems'?: Array<AssociatedItem>;
    /**
     * The item\'s name.
     */
    'Title'?: string;
    /**
     * The number of items in the order. 
     */
    'QuantityOrdered': number;
    /**
     * The number of items shipped.
     */
    'QuantityShipped'?: number;
    'ProductInfo'?: ProductInfoDetail;
    'PointsGranted'?: PointsGrantedDetail;
    'ItemPrice'?: Money;
    'ShippingPrice'?: Money;
    'ItemTax'?: Money;
    'ShippingTax'?: Money;
    'ShippingDiscount'?: Money;
    'ShippingDiscountTax'?: Money;
    'PromotionDiscount'?: Money;
    'PromotionDiscountTax'?: Money;
    /**
     * A list of promotion identifiers provided by the seller when the promotions were created.
     */
    'PromotionIds'?: Array<string>;
    'CODFee'?: Money;
    'CODFeeDiscount'?: Money;
    /**
     * Indicates whether the item is a gift.  **Possible values**: `true` and `false`.
     */
    'IsGift'?: string;
    /**
     * The condition of the item, as described by the seller.
     */
    'ConditionNote'?: string;
    /**
     * The condition of the item.  **Possible values**: `New`, `Used`, `Collectible`, `Refurbished`, `Preorder`, and `Club`.
     */
    'ConditionId'?: string;
    /**
     * The subcondition of the item.  **Possible values**: `New`, `Mint`, `Very Good`, `Good`, `Acceptable`, `Poor`, `Club`, `OEM`, `Warranty`, `Refurbished Warranty`, `Refurbished`, `Open Box`, `Any`, and `Other`.
     */
    'ConditionSubtypeId'?: string;
    /**
     * The start date of the scheduled delivery window in the time zone for the order destination. In [ISO 8601](https://developer-docs.amazon.com/sp-api/docs/iso-8601) date time format.
     */
    'ScheduledDeliveryStartDate'?: string;
    /**
     * The end date of the scheduled delivery window in the time zone for the order destination. In [ISO 8601](https://developer-docs.amazon.com/sp-api/docs/iso-8601) date time format.
     */
    'ScheduledDeliveryEndDate'?: string;
    /**
     * Indicates that the selling price is a special price that is only available for Amazon Business orders. For more information about the Amazon Business Seller Program, refer to the [Amazon Business website](https://www.amazon.com/b2b/info/amazon-business).   **Possible values**: `BusinessPrice`
     */
    'PriceDesignation'?: string;
    'TaxCollection'?: TaxCollection;
    /**
     * When true, the product type for this item has a serial number.   Only returned for Amazon Easy Ship orders.
     */
    'SerialNumberRequired'?: boolean;
    /**
     * When true, the ASIN is enrolled in Transparency. The Transparency serial number that you must submit is determined by:  **1D or 2D Barcode:** This has a **T** logo. Submit either the 29-character alpha-numeric identifier beginning with **AZ** or **ZA**, or the 38-character Serialized Global Trade Item Number (SGTIN). **2D Barcode SN:** Submit the 7- to 20-character serial number barcode, which likely has the prefix **SN**. The serial number is applied to the same side of the packaging as the GTIN (UPC/EAN/ISBN) barcode. **QR code SN:** Submit the URL that the QR code generates.
     */
    'IsTransparency'?: boolean;
    /**
     * The IOSS number of the marketplace. Sellers shipping to the EU from outside the EU must provide this IOSS number to their carrier when Amazon has collected the VAT on the sale.
     */
    'IossNumber'?: string;
    /**
     * The store chain store identifier. Linked to a specific store in a store chain.
     */
    'StoreChainStoreId'?: string;
    /**
     * The category of deemed reseller. This applies to selling partners that are not based in the EU and is used to help them meet the VAT Deemed Reseller tax laws in the EU and UK.
     */
    'DeemedResellerCategory'?: OrderItemDeemedResellerCategoryEnum;
    'BuyerInfo'?: ItemBuyerInfo;
    'BuyerRequestedCancel'?: BuyerRequestedCancel;
    /**
     * A list of serial numbers for electronic products that are shipped to customers. Returned for FBA orders only.
     */
    'SerialNumbers'?: Array<string>;
    'SubstitutionPreferences'?: SubstitutionPreferences;
    'Measurement'?: Measurement;
    'ShippingConstraints'?: ShippingConstraints;
    'AmazonPrograms'?: AmazonPrograms;
}

export const OrderItemDeemedResellerCategoryEnum = {
    Ioss: 'IOSS',
    Uoss: 'UOSS',
} as const;

export type OrderItemDeemedResellerCategoryEnum = typeof OrderItemDeemedResellerCategoryEnum[keyof typeof OrderItemDeemedResellerCategoryEnum];


